import {
  Mapping,
  RelationshipMapping,
} from 'src/containers/ProjectDiagram/types/data';
import {
  Model,
  ModelSnapshot,
} from 'src/containers/ProjectDiagram/types/model';
import {
  isMapping,
  isRelationshipMapping,
} from 'src/containers/ProjectDiagram/utils/data';
import {
  getReasonsForInvalidMapping,
  isValidMapping,
} from 'src/containers/ProjectDiagram/utils/mapping/isValidMapping';
import { getResourceValue } from 'src/containers/ProjectDiagram/utils/resource/createResource';

export const getValidMappingsByResource = (model: Model | ModelSnapshot) => {
  const validClassMappingsByResource: Record<string, Mapping[]> = {};
  const validClassMappingKeys = new Set<string>();
  const validRelationshipMappingsByResource: Record<
    string,
    RelationshipMapping[]
  > = {};

  // need to iterate over class mappings first, since relationship mapping validity
  // is dependent on class mappings
  const classMappings: Mapping[] = [];
  const relationshipMappings: RelationshipMapping[] = [];
  model.linkDataArray.forEach((linkData) => {
    if (isMapping(linkData)) {
      classMappings.push(linkData);
    } else if (isRelationshipMapping(linkData)) {
      relationshipMappings.push(linkData);
    }
  });

  classMappings.forEach((classMapping) => {
    if (!isValidMapping(classMapping)(model)) {
      return;
    }

    const resourceId = getResourceValue(classMapping.from);

    if (!validClassMappingsByResource[resourceId]) {
      validClassMappingsByResource[resourceId] = [];
    }
    validClassMappingsByResource[resourceId].push(classMapping);
    validClassMappingKeys.add(classMapping.key);
  });

  relationshipMappings.forEach((relationshipMapping) => {
    const isValidSelection =
      (relationshipMapping.from &&
        validClassMappingKeys.has(relationshipMapping.from) &&
        relationshipMapping.toClassIri &&
        relationshipMapping.toPrimaryIdentifier) ||
      (relationshipMapping.to &&
        validClassMappingKeys.has(relationshipMapping.to) &&
        relationshipMapping.fromClassIri &&
        relationshipMapping.fromPrimaryIdentifier);
    if (!isValidSelection) {
      return;
    }

    const sourceClassMapping = classMappings.find(
      (mapping) => mapping.key === relationshipMapping.from
    );
    const targetClassMapping = classMappings.find(
      (mapping) => mapping.key === relationshipMapping.to
    );
    const resourceKey = sourceClassMapping?.from || targetClassMapping?.from;
    if (!resourceKey) {
      return;
    }

    const resourceId = getResourceValue(resourceKey);
    if (!validRelationshipMappingsByResource[resourceId]) {
      validRelationshipMappingsByResource[resourceId] = [];
    }
    validRelationshipMappingsByResource[resourceId].push(relationshipMapping);
  });

  return {
    classMappingsByResource: validClassMappingsByResource,
    relationshipMappingsByResource: validRelationshipMappingsByResource,
  };
};

export const getInvalidMappingsByResource = (model: Model | ModelSnapshot) => {
  const invalidMappingsByResource: Record<string, Mapping[]> = {};
  model.linkDataArray.forEach((data) => {
    if (!isMapping(data)) {
      return;
    }

    const maybeReasons = getReasonsForInvalidMapping(data)(model);
    if (!maybeReasons) {
      return;
    }
    const { reasons, quiet } = maybeReasons;
    reasons.forEach((reason) => console.warn(reason));

    if (!quiet) {
      const resourceId = getResourceValue(data.from);
      if (!invalidMappingsByResource[resourceId]) {
        invalidMappingsByResource[resourceId] = [];
      }
      invalidMappingsByResource[resourceId].push(data);
    }
  });

  return invalidMappingsByResource;
};
